/**
 * Eine Stimme je Konto — über alle Geräte. (05.10.2026, Ticket 2882)
 *
 * ## Der Rest, der nach dem 01.10. offen blieb
 *
 * Seit pre.3553 stimmen sich die Seiten DESSELBEN Browsers ab
 * (frontend/lib/seiten-abgleich.ts, BroadcastChannel). Ein zweites Gerät sieht
 * diesen Kanal nicht. Zwei Vorfälle im Journal, beide Rechner + Telefon:
 *
 *   29.09. 07:35  Am Rechner liest der Chat eine getippte Antwort vor
 *                 (393 Zeichen, 07:35:13–07:35:38), am Telefon beginnt um
 *                 07:35:24 die Antwort auf eine Sprachnachricht — 10 s lang
 *                 zwei Stimmen.
 *   01.10. 21:57  Der Rechner liest eine lange Antwort (1830 Zeichen), das
 *                 Telefon holt einen Wach-Wort-Satz nach und spricht ab
 *                 21:57:23 seine eigene Antwort — 19 s lang zwei Stimmen.
 *                 (Nach dem Live-Gang von pre.3553: der Kanal half nicht.)
 *
 * ## Die Regel — dieselbe wie zwischen zwei Seiten
 *
 * Wer zuletzt zu sprechen BEGINNT, spricht. Jede Seite meldet den Beginn
 * ihrer Stimme an den Server (`STIMME_BEGINN_WEG`, Kopf `X-Vespera-Geraet`),
 * der Server vergibt je Konto eine laufende Nummer und reicht das Ereignis
 * über den offenen Kanal jeder Seite des Kontos weiter (`/api/chat/stream`,
 * `event: stimme`). Eine Seite, deren Stimme die kleinere Nummer trägt,
 * endet mit einem Abbruch (`abbruch:anderes-geraet`).
 *
 * ⚠️ Die NUMMER entscheidet, nicht die Uhr der Geräte (die gehen
 *    verschieden) und nicht die Reihenfolge der Ankunft (zwei Verbindungen
 *    überholen einander). Beginnen zwei Geräte im selben Augenblick, hält so
 *    jedes dieselbe für die spätere: im Regelfall bleibt genau eine.
 *
 * ## Grenzen — es ist KEINE Zusage „immer genau eine“ (Nachprüfung 05.10.2026)
 *
 * Mit den echten Bausteinen nachgestellt; die ersten beiden sind seither
 * enger, keine ist ganz zu:
 *
 *  - STILLE, Antwort verloren: beginnen zwei Geräte zugleich und geht die
 *    Antwort an das spätere verloren, kennt es seine Nummer nur noch aus dem
 *    eigenen Ereignis im Kanal. Fehlt auch das, weicht es dem früheren —
 *    das seinerseits schon gewichen ist.
 *  - STILLE, verspätete Meldung: endet eine Stimme, bevor ihre Meldung beim
 *    Server ist, zieht die Seite die Meldung zurück. Ist sie schon dort, hat
 *    der Server sie weitergereicht, und ein Gerät, das inzwischen begann,
 *    weicht einer Stimme, die es nicht mehr gibt. Ein ENDE meldet keine Seite.
 *  - ZWEI STIMMEN, Kanal weg: ein Ereignis, das in eine Neuverbindung des
 *    Kanals fällt (Telefon im Hintergrund, Netzwechsel), wird nicht
 *    nachgeliefert — ohne gemeldetes Ende beendete das eine Stimme, obwohl
 *    das andere Gerät längst fertig ist.
 *  - NICHT ERFASST: Anruf, Kapitel-Vorlesen, Meditation (eigene Spieler ohne
 *    diese Meldung) und eine AUFNAHME auf dem zweiten Gerät (für Wach-Wort und
 *    Auto-Mikro seit 17.10.2026 erfasst — unten „AUFNAHME AUF EINEM ANDEREN GERÄT“).
 *  - Die Regel fragt nicht, WELCHE Stimme die richtige ist: am 01.10. nahm das
 *    Telefon die Stimme des Rechners als Wach-Wort-Satz auf und antwortete
 *    darauf. Hier hätte diese Antwort die echte am Rechner beendet.
 *
 * Hier steht nur, was Server und Seite teilen: Weg, Ereignisname, Form.
 */

/** Der Weg, auf dem eine Seite den Beginn ihrer Stimme meldet. */
export const STIMME_BEGINN_WEG = "/api/stimme/beginn";

/** Name des Ereignisses auf dem Kanal `/api/chat/stream`. */
export const STIMME_EREIGNIS = "stimme";

export interface StimmeEreignis {
  /** Kennung der Seite, deren Stimme begonnen hat (shared/geraet-kennung.ts). */
  geraet: string;
  /** Laufende Nummer je Konto, vom Server vergeben — die größere begann später. */
  nr: number;
  /**
   * 17.10.2026: WAS da zu sprechen begann — die Kennung eines Beitrags (z. B. `schmied:<Sitzung>`), ohne Inhalt. Andere
   * Seiten, die denselben Beitrag zurückhalten (nicht vorn), sprechen ihn dann nicht mehr (frontend/lib/seiten-abgleich.ts
   * „Geparkt“). Fehlt sie, ist es eine gewöhnliche Antwort.
   */
  kennung?: string;
  /**
   * 28.10.2026 (REIHENFOLGE, unten „Besitz“): ein Entwickler-Beitrag, der von selbst zu sprechen begann (kein Abspielknopf). Er
   * löst die laufende Reihe eines anderen Geräts NIE ab. Fehlt es (Vesperas Antwort, Martins Knopf, ältere Seite): wie bisher.
   */
  nachrang?: true;
}

/** Eine Beitrags-Kennung, wenn sie die erlaubte Form hat (kurz, nur Zeichen ohne Inhalt) — sonst null. Rein. */
export function leseStimmeKennung(roh: unknown): string | null {
  return typeof roh === "string" && /^[A-Za-z0-9:._-]{1,120}$/.test(roh) ? roh : null;
}

/** Das Ereignis, wenn es die erwartete Form hat — sonst null. */
export function leseStimmeEreignis(roh: unknown): StimmeEreignis | null {
  if (!roh || typeof roh !== "object") return null;
  const { geraet, nr, kennung, nachrang } = roh as { geraet?: unknown; nr?: unknown; kennung?: unknown; nachrang?: unknown };
  if (typeof geraet !== "string" || geraet.length === 0) return null;
  if (typeof nr !== "number" || !Number.isFinite(nr) || nr <= 0) return null;
  const k = leseStimmeKennung(kennung);
  const e: StimmeEreignis = k ? { geraet, nr, kennung: k } : { geraet, nr };
  if (nachrang === true) e.nachrang = true;
  return e;
}

/*
 * BESITZ (28.10.2026, Martin 07:54: „Während Vespera spricht, funkt plötzlich der Zimmermann hinein, und dann sprang die
 * Audiospur von meinem Computer direkt aufs Handy. Das Ganze braucht eine logische Abfolge.“).
 *
 * Ursache: „Stille“ prüfte jedes Gerät nur bei sich. Am Telefon klang nichts — es las Zimmermanns Beitrag vor (der Mac war
 * nicht als vorn gemeldet: im Hintergrund oder seine Meldung veraltet). Sein Beginn kam als NEUERE Stimme eines ANDEREN
 * Beitrags am Mac an, und dort galt „wer zuletzt beginnt, spricht“: Vesperas laufende Antwort endete (`anderes-geraet`).
 *
 * Seither (Schalter am Server `REIHENFOLGE_WIEDERGABE=0` → wie vorher): das Gerät, das die Wiedergabe BEGANN, besitzt sie
 * bis zum Ende seiner Reihe (Vesperas Antwort und die Entwickler-Beiträge dahinter). Es meldet das im Takt (`BESITZ_TAKT_MS`,
 * Weg `STIMME_BESITZ_WEG`) und am Ende einmal `besitz: false`. Jedes andere Gerät beginnt solange keine Stimme von selbst
 * (Entwickler-Beiträge parken und verfallen dort, wenn der Besitzer sie spricht) — außer der Besitzer meldet sich
 * `BESITZ_GILT_MS` lang nicht (weg: zu, kein Netz, Absturz). Das gilt nur Entwickler-Beiträgen, die von selbst beginnen
 * (`nachrang` am Beginn): Vesperas Antwort (dort, wo Martin fragte) und Martins Abspielknopf gehen wie bisher vor.
 * Nur Kennung, Zustand und eine Nummer — kein Inhalt.
 */

/** Der Weg, auf dem die besitzende Seite ihre Reihe meldet (Rumpf `{ besitz: boolean, reiheNr?: number }`). */
export const STIMME_BESITZ_WEG = "/api/stimme/besitz";
/** So oft erneuert die besitzende Seite ihre Meldung, solange ihre Reihe läuft. */
export const BESITZ_TAKT_MS = 5000;
/** So lange gilt eine Besitz-Meldung ohne Erneuerung — danach gilt das Gerät als weg (drei Takte). */
export const BESITZ_GILT_MS = 15_000;

export interface BesitzEreignis {
  /** Die Seite, deren Reihe läuft (oder endete). */
  geraet: string;
  /** `true`: ihre Reihe läuft. `false`: zu Ende. */
  besitz: boolean;
  /**
   * Die Konto-Nummer des ersten Beginns ihrer Reihe (wie `StimmeEreignis.nr`) — bei zwei Besitzern gewinnt die frühere.
   * Bewusst NICHT `nr`: eine ältere Seite läse das Ereignis sonst als Beginn einer Stimme.
   */
  reiheNr?: number;
  /** Nur beim Nachreichen an eine neu verbundene Seite: so alt ist die letzte Meldung schon (ms). */
  alterMs?: number;
}

/**
 * Teil (a) derselben Regel: Entwickler-Beiträge unterbrechen nie eine laufende Wiedergabe — sie warten in der Reihe, aber
 * höchstens so viele (Ja/Nein-Fragen an Martin zählen nicht mit, sie stehen vorn) und höchstens so lange; danach bleiben sie
 * Blase (Abspielknopf). frontend/lib/vorlese-reihe.ts und seiten-abgleich.ts `stimmeDarfBeginnen`.
 */
export const REIHE_ENTWICKLER_HOECHSTENS = 3;
export const REIHE_ENTWICKLER_WARTEN_MS = 90_000;

/** Eine gültige Reihen-Nummer (endlich, > 0) — sonst null. Rein. */
export function reiheNrSauber(n: unknown): number | null {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
}

/** Das Besitz-Ereignis, wenn es die erwartete Form hat — sonst null. Erkennbar an `besitz` (boolean). */
export function leseBesitzEreignis(roh: unknown): BesitzEreignis | null {
  if (!roh || typeof roh !== "object") return null;
  const { geraet, besitz, reiheNr, alterMs } = roh as { geraet?: unknown; besitz?: unknown; reiheNr?: unknown; alterMs?: unknown };
  if (typeof geraet !== "string" || geraet.length === 0 || typeof besitz !== "boolean") return null;
  const e: BesitzEreignis = { geraet, besitz };
  const n = reiheNrSauber(reiheNr);
  if (n !== null) e.reiheNr = n;
  if (typeof alterMs === "number" && Number.isFinite(alterMs)) e.alterMs = Math.max(0, alterMs);
  return e;
}

/**
 * Zwei Seiten halten sich beide für die Besitzerin: gewinnt die fremde? Die frühere Reihe (kleinere Nummer) gewinnt; ist eine
 * Nummer unbekannt oder gleich, entscheidet die kleinere Kennung — beide Seiten rechnen dasselbe. Rein.
 */
export function besitzFremdGewinnt(fremd: { nr: number | null; geraet: string }, eigen: { nr: number | null; geraet: string }): boolean {
  if (fremd.nr !== null && eigen.nr !== null && fremd.nr !== eigen.nr) return fremd.nr < eigen.nr;
  return fremd.geraet < eigen.geraet;
}

/*
 * VORN (16.10.2026, Martin 08:09: „es soll immer nur das Gerät sprechen, das aktiv benutzt wird“). Eine Seite meldet,
 * dass sie benutzt wird (`an: true`) oder in den Hintergrund ging (`an: false`); der Server reicht es über denselben
 * Kanal (`event: stimme`) an jede Seite des Kontos. Eine Seite, die nicht vorn ist, beginnt keine Stimme — mit Rückfall
 * „sprechen“, wenn niemand vorn ist oder die Meldung veraltet (frontend/lib/seiten-abgleich.ts, „Vorn“).
 * Nur Kennung und Zustand — kein Inhalt, kein Ziel der Eingabe.
 */

/** Der Weg, auf dem eine Seite meldet, dass sie benutzt wird (Rumpf `{ an: boolean, grund?: string }`). */
export const STIMME_VORN_WEG = "/api/stimme/vorn";

export interface VornEreignis {
  /** Die Seite, die sich meldet. */
  geraet: string;
  /** `true`: sie wird benutzt (vorn). `false`: sie ging in den Hintergrund. */
  an: boolean;
  /**
   * 28.10.2026 (Prüfbefund M2): nur mit `an` — die Meldung kam aus ECHTER Bedienung (`vornGrundEcht`: Eingabe, Aufnahme).
   * Martins Hand ist dort; die Reihe eines anderen Geräts („Besitz“) beginnt dann keinen neuen Beitrag. Fehlt es: unbekannt.
   */
  echt?: boolean;
}

/** Das Vorn-Ereignis, wenn es die erwartete Form hat — sonst null. Erkennbar an `an` (ein Beginn trägt `nr`). */
export function leseVornEreignis(roh: unknown): VornEreignis | null {
  if (!roh || typeof roh !== "object") return null;
  const { geraet, an, echt } = roh as { geraet?: unknown; an?: unknown; echt?: unknown };
  if (typeof geraet !== "string" || geraet.length === 0 || typeof an !== "boolean") return null;
  return an && echt === true ? { geraet, an, echt: true } : { geraet, an };
}

/*
 * FERTIG (17.10.2026, Martin 09:45: „die Antwort als abgeschlossen empfinden und den ganzen Vorlese-Prozess stoppen“). Eine
 * Seite meldet, dass Martin „Fertig“ gedrückt hat; der Server reicht es über denselben Kanal an jede Seite des Kontos. Dort
 * werden zurückgehaltene (geparkte) und noch nicht gesprochene Beiträge verworfen, damit nach „Fertig“ nichts nachkommt
 * (frontend/lib/seiten-abgleich.ts `stimmenFertig`). Nur die Kennung — kein Inhalt.
 */

/** Der Weg, auf dem eine Seite „Fertig“ meldet (ohne Rumpf). */
export const STIMME_FERTIG_WEG = "/api/stimme/fertig";

export interface FertigEreignis {
  /** Die Seite, auf der „Fertig“ gedrückt wurde. */
  geraet: string;
  fertig: true;
}

/** Das Fertig-Ereignis, wenn es die erwartete Form hat — sonst null. Erkennbar an `fertig: true` (ohne `nr`, ohne `an`). */
export function leseFertigEreignis(roh: unknown): FertigEreignis | null {
  if (!roh || typeof roh !== "object") return null;
  const { geraet, fertig } = roh as { geraet?: unknown; fertig?: unknown };
  if (typeof geraet !== "string" || geraet.length === 0 || fertig !== true) return null;
  return { geraet, fertig: true };
}

/*
 * HAFTEN (16.10.2026, Martin 18:31: „Jetzt gebe ich gerade die Info über mein MacBook, dann möchte ich, dass der Sound
 * auch dort herauskommt und nicht plötzlich auf dem Handy; umgekehrt genauso.“). Journal 18:2x–18:31: `vorn` sprang
 * binnen Minuten zwischen Mac und iPhone, ausgelöst von `grund=sichtbar` (Bildschirm an, Reiter sichtbar) und
 * `grund=takt` (eine Seite, die die Übergabe verpasst hatte, erneuerte ihr altes „vorn“) — jedes `an: true` machte das
 * meldende Gerät sofort zum vorderen.
 *
 * Seither: nur ECHTE Bedienung (Tippen/Klick/Taste, Sprechtaste, Aufnahme durch einen Menschen oder das Wach-Wort)
 * übernimmt „vorn“ von einem anderen Gerät. Sichtbar, Fokus, Takt und die Aufnahme, die von selbst aufgeht, belegen nur
 * ein FREIES „vorn“: kein Gerät vorn, das vordere ist verschwunden (Seite zu oder über eine Minute stumm), oder es ist im
 * Hintergrund UND seit `VORN_HAFTET_MS` nicht bedient. Server: server/stimme-konto.ts `vorn`; Seite:
 * frontend/lib/seiten-abgleich.ts. Schalter `STIMME_VORN_HAFTET=0` (Server): jede Meldung übernimmt wie vorher.
 */

/** Die Gründe, die echte Bedienung bedeuten — nur sie übernehmen „vorn“ von einem anderen Gerät. */
export const VORN_ECHTE_GRUENDE: readonly string[] = ["eingabe", "aufnahme"];

/** Ist dieser Grund echte Bedienung? Rein. */
export function vornGrundEcht(grund: string): boolean {
  return VORN_ECHTE_GRUENDE.includes(grund);
}

/** So lange haftet „vorn“ an einem Gerät im Hintergrund nach seiner letzten echten Bedienung. */
export const VORN_HAFTET_MS = 10 * 60_000;

/** Das Konto, für das „vorn“ gilt (Martin, Konto 1). Andere Konten: wie bisher, jede Seite spricht. */
export const VORN_KONTO = 1;

/** Gilt „vorn“ für dieses Konto? Rein. */
export function vornGiltFuerKonto(userId: number | null | undefined): boolean {
  return userId === VORN_KONTO;
}

/*
 * AUFNAHME AUF EINEM ANDEREN GERÄT (17.10.2026, Journal 08:16). Martin nahm am Mac per Taste auf (`via=knopf`); mitten im
 * Satz hörte das Wach-Wort am iPhone „Vespera“, öffnete dort eine zweite Aufnahme (`via=wachwort`) und holte sich „vorn“
 * (`grund=aufnahme`) — der Satz kam als zwei Nachrichten von zwei Geräten an. Der Seitenabgleich (BroadcastChannel) endet
 * am Rand des Browsers, „vorn“ sagt nicht, dass ein Gerät gerade AUFNIMMT.
 *
 * Seither meldet eine Seite, die aufnimmt, das dem Server (`STIMME_AUFNAHME_WEG`, Rumpf `{ laeuft, via, lauf }`), erneuert es im
 * Takt (`AUFNAHME_KONTO_TAKT_MS`) und meldet das Ende. Der Server reicht es über denselben Kanal (`event: stimme`) an jede
 * Seite des Kontos. Eine andere Seite startet dann das Wach-Wort und das Auto-Mikro NICHT (frontend/lib/seiten-abgleich.ts
 * `fremdesGeraetNimmtAuf`). Die Meldung verfällt nach `AUFNAHME_KONTO_GILT_MS` ohne Erneuerung — ein abgestürztes Gerät
 * sperrt nichts auf Dauer. Knopf und Sprechtaste bleiben immer möglich (bewusste Bedienung übernimmt „vorn“).
 *
 * Prüfung 17.10.2026:
 *  - `lauf` (Laufnummer der Aufnahme je Seite): Beginn und Ende überholen einander auf dem Netz. Der Server verwirft einen
 *    Beginn/eine Erneuerung, deren Lauf nicht neuer ist als das zuletzt gesehene Ende desselben Geräts — sonst sperrte ein
 *    verspäteter Beginn das andere Gerät 30 s lang ohne Aufnahme.
 *  - `alterMs`: beim Nachreichen an eine neu verbundene Seite das Alter der letzten Meldung — die Sperre hält nie länger als
 *    `AUFNAHME_KONTO_GILT_MS` ab der letzten echten Meldung.
 *  - Nur BEWUSST gestartete Aufnahmen (`AUFNAHME_BEWUSST`) sperren das Wach-Wort am anderen Gerät; eine, die von selbst
 *    aufging (Auto-Mikro, Wach-Wort-Folge), sperrt dort nur das Auto-Mikro.
 * Schalter (Server): `WACHWORT_ZWEITES_GERAET_SPERRE=0` — nichts wird weitergereicht, die Seiten melden nichts mehr.
 */

/** Der Weg, auf dem eine Seite meldet, dass sie aufnimmt (oder nicht mehr). */
export const STIMME_AUFNAHME_WEG = "/api/stimme/aufnahme";
/** So oft erneuert eine aufnehmende Seite ihre Meldung beim Server. */
export const AUFNAHME_KONTO_TAKT_MS = 10_000;
/** So lange gilt die Meldung ohne Erneuerung (drei Takte) — danach blockiert sie nichts mehr. */
export const AUFNAHME_KONTO_GILT_MS = 30_000;

export interface AufnahmeEreignis {
  /** Die Seite, die aufnimmt (oder aufgehört hat). */
  geraet: string;
  /** `true`: nimmt auf. `false`: Aufnahme zu Ende. */
  aufnahme: boolean;
  /** Der Startweg (knopf, wachwort, nach-frage …) — kurzes Wort; entscheidet, ob es das Wach-Wort sperrt. */
  via: string;
  /** Nur beim Nachreichen: so alt ist die letzte Meldung schon (ms). */
  alterMs?: number;
}

/** Startwege, die ein Mensch bewusst auslöst — nur deren Aufnahme sperrt auch das Wach-Wort am anderen Gerät. */
export const AUFNAHME_BEWUSST: readonly string[] = ["knopf", "aktionstaste", "pille", "pille-taste", "freihand"];

/** Eine gültige Laufnummer (ganze Zahl ≥ 0) — sonst null. Rein. */
export function aufnahmeLaufSauber(lauf: unknown): number | null {
  return typeof lauf === "number" && Number.isSafeInteger(lauf) && lauf >= 0 ? lauf : null;
}

/** Ein kurzes Grundwort oder "-". Rein. */
export function aufnahmeViaSauber(via: unknown): string {
  return typeof via === "string" && /^[a-z-]{1,24}$/.test(via) ? via : "-";
}

/** Das Aufnahme-Ereignis, wenn es die erwartete Form hat — sonst null. Erkennbar an `aufnahme` (boolean). */
export function leseAufnahmeEreignis(roh: unknown): AufnahmeEreignis | null {
  if (!roh || typeof roh !== "object") return null;
  const { geraet, aufnahme, via, alterMs } = roh as { geraet?: unknown; aufnahme?: unknown; via?: unknown; alterMs?: unknown };
  if (typeof geraet !== "string" || geraet.length === 0 || typeof aufnahme !== "boolean") return null;
  const e: AufnahmeEreignis = { geraet, aufnahme, via: aufnahmeViaSauber(via) };
  if (typeof alterMs === "number" && Number.isFinite(alterMs)) e.alterMs = Math.max(0, alterMs);
  return e;
}

/**
 * Hat die fremde Stimme Vorrang vor der eigenen?
 *
 * @param eigeneNr die Nummer des eigenen Beginns — `null`, wenn der Server sie
 *                 nie genannt hat (Meldung gescheitert): dann weiß er nichts
 *                 von dieser Stimme, die fremde Seite endet also nicht ihretwegen,
 *                 und es bliebe bei zweien. Darum weicht die eigene.
 * @param gleicherBeitrag die fremde Stimme spricht denselben Beitrag (gleiche Kennung): dann gewinnt die FRÜHERE Nummer.
 */
export function gewinntFremdeKontoStimme(eigeneNr: number | null, fremdeNr: number, gleicherBeitrag = false): boolean {
  // 26.10.2026: derselbe Beitrag (gleiche Kennung) auf zwei Geräten — das ZUERST begonnene spricht weiter, nicht das spätere.
  if (gleicherBeitrag) return eigeneNr === null || fremdeNr < eigeneNr;
  return eigeneNr === null || fremdeNr > eigeneNr;
}
